import Button from 'components/Basic/Button';
import { ModalContainer, ModalContent, ModalFooter, ModalHeader, SettingsPopup } from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { DesignSettings, toggleSettingsModal } from 'store/slices/DesignSettingsSlice';
import { AppDispatch, RootState } from 'store';
import Setback from './Setbacks';
import ConfirmationModal from '../Basic/Modal/Confirmation';
import { useEffect, useRef, useState } from 'react';
import { changePanel } from 'store/slices/RoofDataSlice/roofDataSlice';
import { setApplyingDesignSettings } from 'store/slices/DesignSettingsSlice';
import { inchesToDecimeters } from 'store/slices/DesignSettingsSlice/util';
import { changeSetback } from './api';
import { errorResponseHandler } from 'store/api/AxiosError';
import { appToast } from 'components/Basic/CustomToast';
import { AxiosError } from 'axios';

export default function Settings() {

	const { openModal, setbacks, useEdgeTypeBasedSetbacks , applyingDesignSettings} = useSelector((state: RootState) => state.designSettigs.data);
	const roofData = useSelector((state: RootState) => state.roofData.data);
	const { uuid, panel, imagerySource, designType } = roofData;
	const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
	const [disableApplyBtn, setDisableApplyBtn] = useState<boolean>(false);
	const configuringNewPanel = useSelector((state: RootState) => state.roofData.configuringNewPanel);
	const disptach = useDispatch<AppDispatch>();
	const setbacksRef = useRef<DesignSettings['setbacks']>(setbacks);
	const resetLayoutRef = useRef<boolean>(false);
	const isApplyDisabled = applyingDesignSettings || configuringNewPanel || disableApplyBtn;

	useEffect(()=>{
		setbacksRef.current = setbacks;
	},[setbacks]);

	function handlePopupCancel() {
		resetLayoutRef.current = false;
		disptach(toggleSettingsModal({ open: false }));
	}

	function handleConfirmation() {
		setShowConfirmation(false);
		updateSetbacks();
	}

	async function updateSetbacks(){
		let setbackToSave = {...setbacksRef.current};

		if(!useEdgeTypeBasedSetbacks){
			// for existing designs 
			// i.e no auto edge detection assign non-gutter value to all:
			const edgeBasedSetback = {
				eaves: setbackToSave.gutters,
				rakes: setbackToSave.gutters,
				valleys: setbackToSave.nonGutter,
				ridges: setbackToSave.nonGutter,
				hips: setbackToSave.nonGutter,
			};

			setbackToSave = {
				...setbackToSave,
				...edgeBasedSetback,
			};
		}

		delete setbackToSave.nonGutter;
		for (const key in setbackToSave) {
			const value = setbackToSave[key as keyof typeof setbackToSave];
			if (value !== undefined) {
				setbackToSave[key as keyof typeof setbackToSave] = inchesToDecimeters(value);
			}
		}

		// 1) apply setbacks via the dedicated write endpoint (recomputes + persists the layout).
		//    GET /design-by-uuid no longer applies setbacks, so we must POST first.
		disptach(setApplyingDesignSettings(true)); // cover the POST in the loading state
		try {
			await changeSetback({
				uuid,
				setbacks: setbackToSave as Record<string, number>,
				resetLayout: !!resetLayoutRef.current, // reset now lives on the write endpoint
			});
		} catch (err) {
			// includes 409 E_SHADING_IN_PROGRESS → "Shading is still in progress…" via errorResponseHandler
			disptach(setApplyingDesignSettings(false));
			appToast.error(errorResponseHandler(err as AxiosError).message || 'Could not apply setback changes', {
				toastId: 'change-setback-error',
			});
			resetLayoutRef.current = false;
			return; // don't refetch if the write failed
		}

		await disptach(changePanel({
			uuid, panelKey: panel.key, source: imagerySource,
			designType: designType,
			updateSettings: true,
		}));

		resetLayoutRef.current = false;
	}

	function handleSetbackChnage(
		setback: Partial<DesignSettings['setbacks']>,
		isValid: boolean
	) {
		setDisableApplyBtn(!isValid);

		setbacksRef.current = {
			...setbacksRef.current,
			...setback,
		};
	}

	function handleApplyChanges() {

		if (!hasSetbacksChanged()) {
			handlePopupCancel();
			return;
		}
		resetLayoutRef.current = false;
		updateSetbacks();
	}

	function handleApplyAndResetLayoutClick() {
		resetLayoutRef.current = true;
		setShowConfirmation(true);

	}

	function hasSetbacksChanged() {
		return Object.keys(setbacksRef.current).some((k) => {
			const key = k as keyof DesignSettings['setbacks'];
			return setbacksRef.current[key] !== setbacks[key];
		});
	}

	return (
		<>
			<SettingsPopup
				open={openModal}
				closeOnDocumentClick={false}
			>
				<ModalContainer>
					<ModalHeader>
						Settings
					</ModalHeader>
					<ModalContent>
						<Setback 
							onSetbackChnage={handleSetbackChnage} 
							disableSetbackInput={applyingDesignSettings}
						/>
					</ModalContent>

					<ModalFooter>
						<Button 
							disabled={applyingDesignSettings}
							onClick={handlePopupCancel} 
							style={{ 
								backgroundColor: 'transparent', borderColor: '#7e8183', color: 'var(--subHeadingColor)' 
							}}
						> 
							Cancel
						</Button>
						<Button
							className='resetLayoutButton'
							disabled={isApplyDisabled}
							showLoader={applyingDesignSettings && resetLayoutRef.current}
							onClick={handleApplyAndResetLayoutClick}
						>
								Apply and Reset Layout
						</Button>
						<Button 
							disabled={isApplyDisabled}
							showLoader={applyingDesignSettings && !resetLayoutRef.current}
							onClick={handleApplyChanges} 
							style={{ borderColor: 'var(--primary)' }}
						> 
							Apply 
						</Button>

					</ModalFooter>
				</ModalContainer>

			</SettingsPopup>

			<ConfirmationModal 
				message='Applying these changes will alter the current design layout. Are you sure you want to continue?'
				onCancel={() => setShowConfirmation(false)} 
				onConfirm={handleConfirmation} 
				open={showConfirmation} />
		</>
	);
}